import { HttpClient } from '@angular/common/http';
import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';

import { translate, translateObject } from '@jsverse/transloco';

import moment from 'moment';
import { firstValueFrom } from 'rxjs';

import { FilterService } from 'app/modules/collection/services/filter.service';
import { CustomFieldsService } from 'app/modules/field/custom-fields.service';
import { AuthService } from 'app/providers/bridge-api/auth.service';
import { WidgetDefault } from 'app/providers/bridge-api/dashboard.service';
import { StorageAccountService } from 'app/providers/storage-api/blender-account.service';
import { InteractiveSmartSummaryService } from 'app/providers/storage-api/interactive-smart-summary.service';
import { AlertService } from 'app/services/alert.service';
import {
  injectComponentTelemetryService,
  EnableComponentTelemetry,
  EnableClassTelemetry,
} from 'app/services/otel.service';
import { SystemService } from 'app/services/system.service';

import { TimezoneService } from 'app/services/timezone.service';
import { AbstractWidgetComponent } from '../widget/abstract-widget.component';
import { WidgetComponent } from '../widget/widget.component';

import { WidgetMarkdownEditorComponent } from '../widget-markdown-editor/widget-markdown-editor.component';
import { PptxReport } from '../../services/pptx.service';
import { WidgetCommentsHandlerService } from '../../services/widget-comments-handler.service';

export interface WidgetMarkdownOptions extends WidgetDefault {
  text: string;
}

@EnableClassTelemetry()
@Component({
  selector: 'app-widget-markdown',
  templateUrl: './widget-markdown.component.html',
  styleUrls: ['./widget-markdown.component.scss'],
  standalone: false,
})
export class WidgetMarkdownComponent
  extends AbstractWidgetComponent<WidgetMarkdownOptions>
  implements OnInit, AfterViewInit
{
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  @Input() mode: 'view' | 'edit' = 'view';
  @Output() modeChange = new EventEmitter<'view' | 'edit'>();

  @ViewChild('markdownEditor') markdownEditor: WidgetMarkdownEditorComponent;

  minHeight = 1;

  constructor(
    private httpClient: HttpClient,
    protected systemService: SystemService,
    protected accountService: StorageAccountService,
    protected authService: AuthService,
    protected timezoneService: TimezoneService,
    protected filterService: FilterService,
    protected alertService: AlertService,
    protected interactiveSmartSummaryService: InteractiveSmartSummaryService,
    protected widgetComponent: WidgetComponent,
    protected customFieldService: CustomFieldsService,
    protected commentsHandlerService: WidgetCommentsHandlerService
  ) {
    super(
      systemService,
      accountService,
      authService,
      timezoneService,
      filterService,
      alertService,
      interactiveSmartSummaryService,
      widgetComponent,
      customFieldService,
      commentsHandlerService
    );
    this.needDataSource = false;
  }

  ngOnInit() {
    this.dataSourceNotFound = false;
    this.loadingStarted.emit(true);
  }

  ngAfterViewInit() {
    this.loadingFinished.emit(true);
  }

  get defaultTitle(): string {
    return translate('widgets.note');
  }

  public canShowDataTable() {
    return false;
  }

  public canCreateCollection() {
    return false;
  }

  public get text(): string {
    return this.widget.payload.text;
  }

  public set text(value) {
    this.widget.payload.text = value;
  }

  onEditFinished() {
    this.mode = 'view';
    this.modeChange.emit(this.mode);
    this.loadingFinished.emit();
  }

  public canAddToWidgetGroup() {
    return false;
  }

  public canAddToComparison(): boolean {
    return false;
  }

  public isWidgetGroupChangeDisabled(): boolean {
    return true;
  }

  public canChangeAnalyticItem(): boolean {
    return false;
  }

  public canApplyComparison(_comparison?): boolean {
    return false;
  }

  widgetMenuDisplayOnHover(): boolean {
    return true;
  }

  canHideTitle(): boolean {
    return true;
  }

  get widgetQueryOptions() {
    return null;
  }

  public async exportAsImage(_format: 'blob' | 'canvas' = 'blob', isPpt?: boolean): Promise<Blob> {
    return this.markdownEditor.exportCanvas().then((canvas) => {
      if (!isPpt) {
        canvas = this.createNewCanvasWithTitle(canvas);
      }

      const contentDataURL = canvas.toDataURL('image/svg');

      const body = {
        filename: this.defaultTitle,
        svg: canvas,
      };

      return firstValueFrom(
        this.httpClient.post<Blob>(contentDataURL, body, {
          responseType: 'blob' as 'json',
        })
      );
    });
  }

  public addTitleSlideToPptx(pptx) {
    const translations = translateObject('widgets.pptx');
    const rows = [
      [{ text: translations['widget-name'] }, { text: this.widgetTitle }],
      [{ text: translations['dashboard-name'] }, { text: this.dashboard.title }],
      [{ text: translations['generated'] }, { text: moment().format('LLL') }],
    ];

    const dataOptions = {};
    const tableWidth = 12; // in inches
    this.addSlideOptions(dataOptions);

    pptx.addTableSlide(
      rows,
      {
        data: dataOptions,
        autoPageLineWeight: -0.3,
        w: tableWidth,
        border: {
          color: 'black',
          pt: 2,
          type: 'solid',
        },
      },
      true
    );

    return dataOptions;
  }

  public async exportAsPptx(pptx: PptxReport): Promise<void> {
    this.addTitleSlideToPptx(pptx);
    const canvases = await this.markdownEditor.exportCanvases();
    for (const canvas of canvases) {
      const contentDataURL = canvas.toDataURL('image/svg');
      const body = {
        filename: this.defaultTitle,
        svg: canvas,
      };

      const blob = await firstValueFrom(
        this.httpClient.post<Blob>(contentDataURL, body, {
          responseType: 'blob' as 'json',
        })
      );
      await this.exportImageToPptx(blob, pptx, this.widgetTitle);
    }
  }
}
